import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/** How long typing pauses before a search hits the server. */
export const SEARCH_DEBOUNCE_MS = 300;

/**
 * Search text and filters for a list screen, kept in the URL query string so links and the back
 * button work. The URL is the source of truth for the query. The search box (`ListSearch`)
 * writes `q` once typing settles, replacing the history entry; filters push a new entry.
 */
export function useListUrlState() {
  const [params, setParams] = useSearchParams();

  const update = useCallback(
    (changes: Record<string, string | null>, replace: boolean) =>
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(changes)) {
            if (v) next.set(k, v);
            else next.delete(k);
          }
          return next;
        },
        { replace },
      ),
    [setParams],
  );

  return {
    /** The settled search, as sent to the server. */
    q: params.get('q') || undefined,
    setQ: useCallback((q: string) => update({ q: q || null }, true), [update]),
    get: (key: string) => params.get(key) ?? undefined,
    setFilter: (key: string, value: string | null) => update({ [key]: value }, false),
  };
}
